'use client'

import { useListProjects } from '@repo/services'
import { useLocale } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef } from 'react'

// List of pages to preload (relative paths without locale)
const PAGES_TO_PRELOAD = [
  '/rwa/',
  '/wallet/',
  '/club/invite/',
  '/club/missions/',
  '/club/rewards/',
  '/quick-trade/',
  '/auth/login/',
  '/auth/register/',
  '/profile/account/',
  '/profile/cards/',
]

export function PreloadPages() {
  const locale = useLocale()
  const router = useRouter()
  const isLoaded = useRef(false)

  const { data } = useListProjects({
    skip: 0,
    limit: 10,
    kind: 'property',
  })

  const projects = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.data?.projects || [])
        ?.map((x) => `/rwa/${x.token}/`) || [],
    [data],
  )

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      // Only preload in production or when service worker is available
      if (
        typeof window === 'undefined' ||
        !('serviceWorker' in navigator) ||
        !data?.pages ||
        data?.pages?.length <= 0 ||
        isLoaded.current
      ) {
        return
      }

      isLoaded.current = true

      // Function to preload a page
      const preloadPage = async (url: string) => {
        try {
          // Use link prefetch for better performance
          const link = document.createElement('link')
          link.rel = 'prefetch'
          link.href = url
          link.as = 'document'
          document.head.appendChild(link)

          // Also fetch the page to cache it in service worker
          // Use low priority to not block other requests
          if ('requestIdleCallback' in window) {
            requestIdleCallback(
              () => {
                fetch(url, {
                  method: 'GET',
                  headers: {
                    Accept: 'text/html',
                    cache: 'force-cache',
                    'Cache-Control': 'force-cache',
                  },
                }).catch(() => {
                  // Silently fail if preload fails
                })
              },
              { timeout: 5000 },
            )
          } else {
            // Fallback for browsers without requestIdleCallback
            setTimeout(() => {
              fetch(url, {
                method: 'GET',
                headers: {
                  Accept: 'text/html',
                  cache: 'force-cache',
                  'Cache-Control': 'force-cache',
                },
              }).catch(() => {
                // Silently fail if preload fails
              })
            }, 1000)
          }
        } catch (error) {
          // Silently fail if preload fails
          console.debug('Failed to preload page:', url, error)
        }
      }

      setTimeout(() => {
        // Preload all pages for the current locale
        const baseUrl = window.location.origin
        const temp = [...PAGES_TO_PRELOAD, ...projects]
        temp.forEach((page) => {
          const path = `/${locale}${page}`
          const fullUrl = `${baseUrl}${path}`

          // Use Next.js router prefetch for RSC cache
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => router.prefetch(path), { timeout: 5000 })
          } else {
            setTimeout(() => router.prefetch(path), 1000)
          }

          preloadPage(fullUrl)
        })

        // Also notify service worker to preload pages
        if (
          'serviceWorker' in navigator &&
          navigator.serviceWorker.controller
        ) {
          navigator.serviceWorker.controller.postMessage({
            type: 'PRELOAD_PAGES',
            locale,
          })
        }

        // Wait for service worker to be ready and then send message
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then((registration) => {
            if (registration.active) {
              registration.active.postMessage({
                type: 'PRELOAD_PAGES',
                locale,
              })
            }
          })
        }
      }, 2000)
    }
  }, [locale, projects, router, data?.pages])

  return null
}
